import { useState, useCallback, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../../redux/store'; // Adjust path as needed
import { getValidToken } from '../../../redux/slices/userSlice';
import { UseAdminApiReturn } from '../types/adminApiTypes';
import { handleTokenError } from '../../../utils/tokenErrorHandler';

// Backend URL is now injected from Secret Manager during build process
const BACKEND_BASE_URL = process.env.REACT_APP_BACKEND_BASE_URL;

if (!BACKEND_BASE_URL) {
  throw new Error('REACT_APP_BACKEND_BASE_URL environment variable is required but not set');
}

const ADMIN_API_PATH = '/api/v1/admin'; // Path specific to admin endpoints

export function useAdminApi<T>(): UseAdminApiReturn<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Tracks the in-flight request so we can cancel it when a new one starts.
  const abortControllerRef = useRef<AbortController | null>(null);

  // Abort any in-flight request when the hook unmounts.
  useEffect(() => {
    return () => { abortControllerRef.current?.abort(); };
  }, []);

  // Use Redux dispatch to get valid tokens with automatic refresh
  const dispatch = useDispatch<AppDispatch>();
  const isAuthenticated = useSelector((state: RootState) => state.user.isAuthenticated);

  // Updated fetchData function with automatic token refresh
  const fetchDataInternal = useCallback(async (endpoint: string, params?: Record<string, any>, method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET', body?: Record<string, any>) => {
    // Cancel any previous in-flight request before starting a new one.
    // This prevents stale responses from overwriting fresher data (e.g. from
    // React Strict Mode double-mount or rapid param changes).
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;

    // Check authentication state first
    if (!isAuthenticated) {
      setError('User not authenticated. Please sign in.');
      return;
    }

    setLoading(true);
    setError(null);
    setData(null); // Clear previous data on new fetch

    let aborted = false;
    let fullUrl = ''; // Define fullUrl outside try block for use in error message
    try {
      // Get a fresh, valid token with enhanced fallback handling
      let token: string;
      try {
        token = await dispatch(getValidToken()).unwrap();
      } catch (tokenError) {
        const errorMessage = await handleTokenError(tokenError, {
          operation: `admin-api-${endpoint}`,
          operationType: 'admin-api',
          enableFallback: true,
          showUserNotification: true,
          dispatch: dispatch,
          metadata: { endpoint, method, params }
        });
        console.error('[useAdminApi] Token refresh failed:', tokenError);
        setError(errorMessage);
        return;
      }
      
      // Construct the full URL
      fullUrl = `${BACKEND_BASE_URL}${ADMIN_API_PATH}${endpoint}`;

      // Append query parameters for GET requests
      if (params && method === 'GET' && Object.keys(params).length > 0) { // Check if params exist
        const urlParams = new URLSearchParams();
        
        // Handle each parameter, including arrays for repeated query params.
        // Skip null/undefined so they are never serialised as the string "null".
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            if (Array.isArray(value)) {
              value.forEach(item => urlParams.append(key, String(item)));
            } else {
              urlParams.append(key, String(value));
            }
          }
        });

        const query = urlParams.toString();
        if (query) {
          fullUrl += `?${query}`;
        }
      }

      console.log(`[useAdminApi] Fetching: ${method} ${fullUrl}`); // Log the full URL being fetched

      const options: RequestInit = {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      };

      if (body && method !== 'GET') {
        options.body = JSON.stringify(body);
      }

      const response = await fetch(fullUrl, { ...options, signal });

      // Try to get error details even if response.ok is false
      let errorData: any = null;
      if (!response.ok) {
          try {
              // Attempt to parse error response as JSON, regardless of status code
              errorData = await response.json();
              console.error('[useAdminApi] Backend Error Response Body:', errorData);
          } catch (e) {
              console.warn('[useAdminApi] Could not parse error response body as JSON.');
          }
      }

      if (!response.ok) {
          // Construct a more informative error message
          let errorMsg = `HTTP error! Status: ${response.status} from ${method} ${fullUrl}`;
          // Use detail from parsed error data if available, otherwise use the generic message
          errorMsg = errorData?.detail || errorMsg;
          throw new Error(errorMsg); // Throw the error to be caught below
      }

      // If response is OK, parse the successful result
      const result = await response.json();
      console.log(`[useAdminApi] Successful Response Data (${method} ${fullUrl}):`, result);
      setData(result);

    } catch (err: any) {
      // AbortError means we intentionally cancelled this request (a newer one
      // started). Don't update state — the newer request will handle it.
      // Set aborted=true BEFORE returning so the finally block skips setLoading(false),
      // which would otherwise cancel the loading spinner of the newer request.
      if (err.name === 'AbortError') { aborted = true; return; }
      console.error('[useAdminApi] API Fetch Error:', err);
      // Set the error state using the message from the thrown error
      setError(err.message || 'An unexpected error occurred during fetch.');
      setData(null); // Ensure data is null on error
    } finally {
      if (!aborted) setLoading(false);
    }
  }, [dispatch, isAuthenticated]); // Dependencies: dispatch for token refresh, isAuthenticated for auth state

  // Wrapper to match the expected interface signature
  const fetchDataWrapper = useCallback(async (fetchParams?: Record<string, any>, fetchBody?: Record<string, any>) => {
    const endpoint = fetchParams?.endpoint as string || '';
    const queryParams = { ...fetchParams };
    delete queryParams.endpoint; // Remove endpoint from query params

    // Assuming GET for simplicity based on current usage, adjust if POST/etc. needed via this wrapper
    await fetchDataInternal(endpoint, queryParams, 'GET', fetchBody);
  }, [fetchDataInternal]);

  return { data, loading, error, fetchData: fetchDataWrapper };
} 